import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaChartPie, FaClipboardList, FaTruck, FaCheckCircle, 
  FaCog, FaUser, FaSignOutAlt, FaEnvelope, FaBell,
  FaSearch, FaQuestionCircle, FaPlusCircle, FaHistory,
  FaFileInvoice, FaUpload, FaDollarSign, FaClock,
  FaChartLine, FaGavel, FaBoxes, FaFileAlt, FaMapMarkerAlt,
  FaLifeRing, FaFolderOpen
} from 'react-icons/fa';
import { API_URL } from '../config';
import NotificationBell from '../components/NotificationBell';
import MessagesDropdown from '../components/MessagesDropdown';
import SubbieProfileDropdown from '../components/SubbieProfileDropdown';
import SupportModal from '../components/SupportModal';

function SubbieLayout({ children, activeTab, onLogout, user }) {
  const currentUser = user || JSON.parse(localStorage.getItem('user') || '{}');
  // PHASE 5 — a "projects only" login sees just the project area (nav items are
  // hidden by the .portal-projects-only CSS rules; the route guard blocks URLs).
  const projectsOnly = currentUser?.portal_scope === 'projects_only';
  const [subbieId, setSubbieId] = useState(null);
  const [counts, setCounts] = useState({
    newJobs: 0,
    activeJobs: 0,
    completedJobs: 0,
    loads: 0,
    invoices: 0
  });
  const [companyName, setCompanyName] = useState('');
  const [supportOpen, setSupportOpen] = useState(false);

  useEffect(() => {
    const getSubbieId = async () => {
      try {
        let savedId = localStorage.getItem('subbieId');
        if (savedId) {
          setSubbieId(savedId);
          await fetchCounts(savedId);
          await fetchCompanyName(savedId);
          return;
        }

        const response = await fetch(`${API_URL}/subcontractors?user_id=${currentUser.id}`);
        const data = await response.json();
        if (data.success && data.subcontractors && data.subcontractors.length > 0) {
          const id = data.subcontractors[0].id;
          setSubbieId(id);
          localStorage.setItem('subbieId', id);
          setCompanyName(data.subcontractors[0].company_name || '');
          await fetchCounts(id);
        }
      } catch (err) {
        console.error('Error fetching subbie ID:', err);
      }
    };

    if (currentUser.id) {
      getSubbieId();
    }
  }, [currentUser.id]);

  const fetchCounts = async (id) => {
    try {
      const response = await fetch(`${API_URL}/subbie/all-requirements/${id}`);
      const data = await response.json();
      if (data.success) {
        const requirements = data.requirements || [];
        const newJobs = requirements.filter(r => r.status === 'pending' || r.status === 'quoting').length;
        const activeJobs = requirements.filter(r => r.status === 'approved' || r.status === 'accepted' || r.status === 'assigned').length;
        const completedJobs = requirements.filter(r => r.status === 'completed' || r.status === 'closed').length;
        
        setCounts(prev => ({
          ...prev,
          newJobs,
          activeJobs,
          completedJobs
        }));
      }

      try {
        const loadResponse = await fetch(`${API_URL}/fleet/allocations/${id}?status=pending,on_route_to_loading,onsite,loading,en_route,at_destination,delivered`);
        const loadData = await loadResponse.json();
        if (loadData.success) {
          const activeLoads = loadData.allocations?.filter(l => l.status !== 'completed') || [];
          setCounts(prev => ({
            ...prev,
            loads: activeLoads.length || 0
          }));
        }
      } catch (loadErr) {
        console.error('Error fetching load counts:', loadErr);
      }

      try {
        const invoiceResponse = await fetch(`${API_URL}/subbie-invoices/count/${id}`);
        const invoiceData = await invoiceResponse.json();
        if (invoiceData.success) {
          const badgeCount = (invoiceData.readyForPOD || 0) + (invoiceData.pending || 0);
          setCounts(prev => ({
            ...prev,
            invoices: badgeCount || 0
          }));
        }
      } catch (invErr) {
        console.error('Error fetching invoice counts:', invErr);
      }
    } catch (err) {
      console.error('Error fetching counts:', err);
    }
  };

  const fetchCompanyName = async (id) => {
    try {
      const response = await fetch(`${API_URL}/subcontractors/${id}`);
      const data = await response.json();
      if (data.success) {
        setCompanyName(data.subcontractor?.company_name || '');
      }
    } catch (err) {
      console.error('Error fetching company name:', err);
    }
  };

  const getActiveBg = (tab) => {
    return activeTab === tab ? '#00A86B' : 'transparent';
  };

  const getActiveColor = (tab) => {
    return activeTab === tab ? '#0A1929' : '#94A3B8';
  };

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#F8F9FA' }}>
      {/* Sidebar */}
      <div className="w-64 text-white fixed h-full overflow-y-auto" style={{ backgroundColor: '#0A1929' }}>
        {/* ✅ TEXT LOGO - Subbie */}
        <div className="p-6 border-b" style={{ borderColor: '#1E2F3F' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg" style={{ backgroundColor: '#00A86B', color: '#0A1929' }}>
              <span className="text-sm">OV</span>
            </div>
            <div className="font-bold text-lg">
              ONE<span style={{ color: '#00A86B' }}>VIEW</span>
            </div>
          </div>
          <p className="text-xs mt-2" style={{ color: '#94A3B8' }}>Subbie Portal</p>
          {companyName && (
            <p className="text-xs mt-1 font-medium" style={{ color: '#00A86B' }}>{companyName}</p>
          )}
          {projectsOnly && (
            <p className="text-[10px] mt-2 inline-block px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(0,168,107,0.15)', color: '#00A86B' }}>
              Projects access
            </p>
          )}
        </div>

        <nav className={`p-4${projectsOnly ? ' portal-projects-only' : ''}`}>
          <p className="text-xs uppercase tracking-wider px-3 mb-3" style={{ color: '#94A3B8' }}>Main</p>
          
          <Link 
            to="/subbie-dashboard"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('dashboard'),
              color: getActiveColor('dashboard')
            }}
          >
            <FaChartPie size={18} /> Dashboard
          </Link>
          
          <Link 
            to="/subbie-new-jobs"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('newjobs'),
              color: getActiveColor('newjobs')
            }}
          >
            <FaClipboardList size={18} /> New Jobs
            {counts.newJobs > 0 && (
              <span className="ml-auto px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: '#F59E0B', color: '#0A1929' }}>
                {counts.newJobs}
              </span>
            )}
          </Link>
          
          <Link
            to="/subbie-projects"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{
              backgroundColor: getActiveBg('projects'),
              color: getActiveColor('projects')
            }}
          >
            <FaFolderOpen size={18} /> Projects
          </Link>

          <Link 
            to="/subbie-active-jobs"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('activejobs'),
              color: getActiveColor('activejobs')
            }}
          >
            <FaTruck size={18} /> Active Jobs
            {counts.activeJobs > 0 && (
              <span className="ml-auto px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: '#3B82F6', color: 'white' }}>
                {counts.activeJobs}
              </span>
            )}
          </Link>
          
          <Link 
            to="/subbie-closed-jobs"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('closed'),
              color: getActiveColor('closed')
            }}
          >
            <FaCheckCircle size={18} /> Completed Jobs
            {counts.completedJobs > 0 && (
              <span className="ml-auto px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: '#00A86B', color: '#0A1929' }}>
                {counts.completedJobs}
              </span>
            )}
          </Link>

          <Link 
            to="/subbie-tenders"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('tenders'),
              color: getActiveColor('tenders')
            }}
          >
            <FaGavel size={18} /> Tender Board
          </Link>

          <div className="border-t my-3" style={{ borderColor: '#1E2F3F' }}></div>
          
          <p className="text-xs uppercase tracking-wider px-3 mb-3" style={{ color: '#94A3B8' }}>Fleet & Operations</p>
          
          <Link 
            to="/subbie-loads"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('loads'),
              color: getActiveColor('loads')
            }}
          >
            <FaBoxes size={18} /> Loads
            {counts.loads > 0 && (
              <span className="ml-auto px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: '#8B5CF6', color: 'white' }}>
                {counts.loads}
              </span>
            )}
          </Link>
          
          <Link 
            to="/subbie-fleet"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('fleet'),
              color: getActiveColor('fleet')
            }}
          >
            <FaTruck size={18} /> Manage Fleet
          </Link>
          
          <Link 
            to="/subbie-live-map"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('livemap'),
              color: getActiveColor('livemap')
            }}
          >
            <FaMapMarkerAlt size={18} /> Live Map
          </Link>
          
          <Link 
            to="/subbie-uploads"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('uploads'),
              color: getActiveColor('uploads')
            }}
          >
            <FaUpload size={18} /> Uploads
          </Link>

          <div className="border-t my-3" style={{ borderColor: '#1E2F3F' }}></div>
          
          <p className="text-xs uppercase tracking-wider px-3 mb-3" style={{ color: '#94A3B8' }}>Finance & Reports</p>
          
          <Link 
            to="/subbie-invoices"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('invoices'),
              color: getActiveColor('invoices')
            }}
          >
            <FaFileInvoice size={18} /> Invoices
            {counts.invoices > 0 && (
              <span className="ml-auto px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: '#8B5CF6', color: 'white' }}>
                {counts.invoices}
              </span>
            )}
          </Link>
          
          <Link 
            to="/subbie-reports"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('reports'),
              color: getActiveColor('reports')
            }}
          >
            <FaChartLine size={18} /> Reports
          </Link>

          <div className="border-t my-3" style={{ borderColor: '#1E2F3F' }}></div>
          
          <p className="text-xs uppercase tracking-wider px-3 mb-3" style={{ color: '#94A3B8' }}>Account</p>
          
          <Link 
            to="/subbie-profile"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('profile'),
              color: getActiveColor('profile')
            }}
          >
            <FaUser size={18} /> Profile
          </Link>
          
          <Link 
            to="/subbie-history"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer`}
            style={{ 
              backgroundColor: getActiveBg('history'),
              color: getActiveColor('history')
            }}
          >
            <FaHistory size={18} /> History
          </Link>

          <div className="border-t my-3" style={{ borderColor: '#1E2F3F' }}></div>

          <button
            onClick={() => setSupportOpen(true)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer"
            style={{ color: '#94A3B8' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#00A86B'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}
          >
            <FaLifeRing size={18} /> Support
          </button>

          <div className="border-t my-3" style={{ borderColor: '#1E2F3F' }}></div>
          
          <button 
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors cursor-pointer"
            style={{ color: '#94A3B8' }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#EF4444'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#94A3B8'}
          >
            <FaSignOutAlt size={18} /> Logout
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <div className="ml-64 flex-1 min-w-0 max-w-full">
        {/* Top Bar */}
        <div className="border-b px-8 py-4 flex justify-between items-center sticky top-0 z-10" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8ECEF' }}>
          <div className="flex items-center gap-3">
            <span className="text-sm" style={{ color: '#4A5568' }}>{new Date().toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-3">
            <MessagesDropdown userId={currentUser?.id} />
            <NotificationBell userId={currentUser?.id} />
            <SubbieProfileDropdown user={currentUser} onLogout={onLogout} />
          </div>
        </div>

        {/* Page Content */}
        <div className="p-8">
          {children}
        </div>
      </div>
      <SupportModal isOpen={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  );
}

export default SubbieLayout;